import { useEffect, useMemo, useRef, useState } from 'react'
import {
  LayoutChangeEvent,
  Modal,
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
} from 'react-native'
import Svg, { Line, Path, Rect } from 'react-native-svg'
import { colors } from '@/src/theme'
import { body3, body4, label3 } from '@/src/theme/typography'
import { useTranslation } from 'react-i18next'

import type {
  VoiceAssessmentRangeBand,
  VoiceAssessmentRangePoint,
} from '../../utils/voice-assessment-range-series'
import type { VoiceAssessmentChartXLabel } from '../../utils/voice-assessment-score-series'
import {
  clampChartOverlayValue,
  formatVoiceAssessmentChartDate,
  noteLabelForPosition,
} from './voiceAssessmentChartOverlay'

const CHART_HEIGHT = 120
const Y_AXIS_WIDTH = 34
const PADDING_X = 4
const PADDING_Y = 6
const X_AXIS_LABEL_HEIGHT = 48
const POINT_HIT_SIZE = 44
const OVERLAY_WIDTH = 224
const OVERLAY_HEIGHT = 88
const OVERLAY_MARGIN = 12
/** Wider band — Figma fill `#FFB10433`, stroke 1px Core Primary. */
const RANGE_OUTER_FILL = colors.corePrimaryFill
const RANGE_OUTER_STROKE = colors.corePrimary
/** Narrower band — Figma fill `#E30B5D33`, stroke Core Pink. */
const RANGE_INNER_FILL = colors.corePinkFill
const RANGE_INNER_STROKE = colors.corePink
const GRID_COLOR = colors.border
const OUTER_STROKE_WIDTH = 1
/** Slightly stronger than outer (Figma: pink edge reads a bit heavier). */
const INNER_STROKE_WIDTH = 2
const Y_TICK_COUNT = 7
const ISOLATED_BAND_WIDTH = 4

interface VoiceAssessmentStatisticsRangeBandChartProps {
  points: VoiceAssessmentRangePoint[]
  xLabels: VoiceAssessmentChartXLabel[]
  visibleLabelCount: number
  columnWidthMultiplier: number
  totalDays: number
  locale: string
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n))
}

function buildXLabelSlots({
  xLabels,
  plotContentWidth,
}: {
  xLabels: VoiceAssessmentChartXLabel[]
  plotContentWidth: number
}) {
  const parsedLabels = xLabels.map(parseXLabel)
  const visibleIndexes = parsedLabels.flatMap((label, index) =>
    label.line1.length > 0 || (label.line2?.length ?? 0) > 0 ? [index] : [],
  )
  const minVisibleSpacing = visibleIndexes.reduce<number | null>(
    (minSpacing, index, visibleIndex) => {
      const previous = visibleIndexes[visibleIndex - 1]
      if (previous == null) {
        return minSpacing
      }
      const spacing = index - previous
      return minSpacing == null ? spacing : Math.min(minSpacing, spacing)
    },
    null,
  )
  const innerW = Math.max(0, plotContentWidth - 2 * PADDING_X)
  const stepWidth = xLabels.length > 1 ? innerW / (xLabels.length - 1) : innerW
  const slotWidth = Math.min(
    plotContentWidth,
    Math.max(32, stepWidth * Math.max(1, minVisibleSpacing ?? 1)),
  )

  return visibleIndexes.map((index) => {
    const centerX =
      xLabels.length <= 1 ? plotContentWidth / 2 : PADDING_X + stepWidth * index
    return {
      ...parsedLabels[index],
      index,
      left: centerX - slotWidth / 2,
      width: slotWidth,
    }
  })
}

function parseXLabel(label: VoiceAssessmentChartXLabel | undefined): {
  line1: string
  line2?: string
} {
  if (label == null) {
    return { line1: '' }
  }
  if (typeof label === 'string') {
    return { line1: label }
  }
  return { line1: label.line1, line2: label.line2 }
}

function collectRangeExtents(points: readonly VoiceAssessmentRangePoint[]): {
  min: number
  max: number
} {
  const values = points.flatMap((point) => [
    point.pitch?.min,
    point.pitch?.max,
    point.comfortable?.min,
    point.comfortable?.max,
  ])
  const numericValues = values.filter((value): value is number => value != null)
  if (numericValues.length === 0) {
    return { min: 9 + 4 * 12, max: 7 + 5 * 12 }
  }

  const min = Math.min(...numericValues)
  const max = Math.max(...numericValues)
  return {
    min: min - 1,
    max: max + 1,
  }
}

function yForPosition({
  position,
  yMin,
  yMax,
}: {
  position: number
  yMin: number
  yMax: number
}): number {
  const span = Math.max(1, yMax - yMin)
  const plotH = CHART_HEIGHT - PADDING_Y * 2
  return PADDING_Y + ((yMax - position) / span) * plotH
}

type RangeBandKey = 'pitch' | 'comfortable'

interface RangeBandPointGeometry {
  point: VoiceAssessmentRangePoint
  band: VoiceAssessmentRangeBand
  x: number
  yTop: number
  yBottom: number
}

interface RangeBandPathGeometry {
  id: string
  fillD: string
  topD: string
  bottomD: string
}

interface RangeBandRectGeometry {
  id: string
  x: number
  y: number
  width: number
  height: number
}

function bandRectForPoint({
  point,
  band,
  plotContentWidth,
  yMin,
  yMax,
}: {
  point: VoiceAssessmentRangePoint
  band: VoiceAssessmentRangeBand
  plotContentWidth: number
  yMin: number
  yMax: number
}): RangeBandRectGeometry {
  const innerW = Math.max(0, plotContentWidth - 2 * PADDING_X)
  const centerX = PADDING_X + clamp(point.x, 0, 1) * innerW
  const yTop = yForPosition({ position: band.max, yMin, yMax })
  const yBottom = yForPosition({ position: band.min, yMin, yMax })
  const width = Math.min(ISOLATED_BAND_WIDTH, innerW)

  return {
    id: `${point.segmentId}-${String(point.dayIndex)}`,
    x: clamp(centerX - width / 2, PADDING_X, PADDING_X + innerW - width),
    y: yTop,
    width,
    height: Math.max(2, yBottom - yTop),
  }
}

function buildRangeBandPathGeometry(
  points: readonly RangeBandPointGeometry[],
): RangeBandPathGeometry {
  const topPoints = points.map(
    (point) => `${String(point.x)},${String(point.yTop)}`,
  )
  const bottomPoints = [...points]
    .reverse()
    .map((point) => `${String(point.x)},${String(point.yBottom)}`)
  const bottomStrokePoints = points.map(
    (point) => `${String(point.x)},${String(point.yBottom)}`,
  )

  return {
    id: `${points[0]?.point.segmentId ?? '0'}-${String(
      points[0]?.point.dayIndex ?? 0,
    )}`,
    fillD: `M ${topPoints.join(' L ')} L ${bottomPoints.join(' L ')} Z`,
    topD: `M ${topPoints.join(' L ')}`,
    bottomD: `M ${bottomStrokePoints.join(' L ')}`,
  }
}

function buildBandGeometry({
  points,
  bandKey,
  plotContentWidth,
  yMin,
  yMax,
}: {
  points: readonly VoiceAssessmentRangePoint[]
  bandKey: RangeBandKey
  plotContentWidth: number
  yMin: number
  yMax: number
}): {
  paths: RangeBandPathGeometry[]
  rects: RangeBandRectGeometry[]
} {
  const innerW = Math.max(0, plotContentWidth - 2 * PADDING_X)
  const paths: RangeBandPathGeometry[] = []
  const rects: RangeBandRectGeometry[] = []
  let currentSegment: RangeBandPointGeometry[] = []

  const flushSegment = () => {
    if (currentSegment.length === 0) {
      return
    }
    if (currentSegment.length === 1) {
      const [segmentPoint] = currentSegment
      rects.push(
        bandRectForPoint({
          point: segmentPoint.point,
          band: segmentPoint.band,
          plotContentWidth,
          yMin,
          yMax,
        }),
      )
    } else {
      paths.push(buildRangeBandPathGeometry(currentSegment))
    }
    currentSegment = []
  }

  points.forEach((point) => {
    const band = point[bandKey]
    if (band == null) {
      flushSegment()
      return
    }

    const previous = currentSegment.at(-1)
    if (
      previous != null &&
      (point.segmentId !== previous.point.segmentId ||
        point.dayIndex !== previous.point.dayIndex + 1)
    ) {
      flushSegment()
    }

    const x = PADDING_X + clamp(point.x, 0, 1) * innerW
    currentSegment.push({
      point,
      band,
      x,
      yTop: yForPosition({ position: band.max, yMin, yMax }),
      yBottom: yForPosition({ position: band.min, yMin, yMax }),
    })
  })
  flushSegment()

  return { paths, rects }
}

function rangeLabel(band: VoiceAssessmentRangeBand | undefined): string {
  if (band == null) {
    return '-'
  }
  return `${noteLabelForPosition(band.min)} - ${noteLabelForPosition(band.max)}`
}

export function VoiceAssessmentStatisticsRangeBandChart({
  points,
  xLabels,
  visibleLabelCount,
  columnWidthMultiplier,
  totalDays,
  locale,
}: VoiceAssessmentStatisticsRangeBandChartProps) {
  const { t } = useTranslation(['statistics'])
  const scrollViewRef = useRef<ScrollView>(null)
  const chartViewportRef = useRef<View>(null)
  const scrollXRef = useRef(0)
  const [plotViewportWidth, setPlotViewportWidth] = useState(0)
  const { width: windowWidth, height: windowHeight } = useWindowDimensions()
  const [selectedPoint, setSelectedPoint] = useState<{
    point: VoiceAssessmentRangePoint
    anchorX: number
    anchorY: number
    viewportX: number
    viewportY: number
    scrollX: number
  } | null>(null)

  const onChartLayout = (event: LayoutChangeEvent) => {
    const next = event.nativeEvent.layout.width
    if (next > 0) {
      setPlotViewportWidth(next)
    }
  }

  const plotContentWidth = useMemo(() => {
    if (plotViewportWidth <= 0) {
      return 0
    }

    const visibleColumnCount = Math.max(1, visibleLabelCount)
    const totalColumnCount = Math.max(1, xLabels.length - 1)
    const baseWidth =
      (plotViewportWidth / visibleColumnCount) * totalColumnCount
    return Math.max(
      plotViewportWidth,
      Math.floor(baseWidth * columnWidthMultiplier),
    )
  }, [
    columnWidthMultiplier,
    plotViewportWidth,
    visibleLabelCount,
    xLabels.length,
  ])

  const {
    yMin,
    yMax,
    yTicks,
    pitchPaths,
    pitchRects,
    comfortablePaths,
    comfortableRects,
    hitTargets,
  } = useMemo(() => {
    const extents = collectRangeExtents(points)
    const span = Math.max(1, extents.max - extents.min)
    const tickPositions = Array.from({ length: Y_TICK_COUNT }, (_, index) =>
      Math.round(extents.max - (span * index) / (Y_TICK_COUNT - 1)),
    )
    const innerW = Math.max(0, plotContentWidth - 2 * PADDING_X)
    const columnCount = Math.max(1, totalDays - 1)
    const columnWidth = Math.max(2, innerW / columnCount)

    if (plotContentWidth <= 0 || points.length === 0) {
      return {
        yMin: extents.min,
        yMax: extents.max,
        yTicks: tickPositions,
        pitchPaths: [] as RangeBandPathGeometry[],
        pitchRects: [] as RangeBandRectGeometry[],
        comfortablePaths: [] as RangeBandPathGeometry[],
        comfortableRects: [] as RangeBandRectGeometry[],
        hitTargets: [] as {
          point: VoiceAssessmentRangePoint
          centerX: number
          x: number
          width: number
        }[],
      }
    }

    const pitchGeometry = buildBandGeometry({
      points,
      bandKey: 'pitch',
      plotContentWidth,
      yMin: extents.min,
      yMax: extents.max,
    })
    const comfortableGeometry = buildBandGeometry({
      points,
      bandKey: 'comfortable',
      plotContentWidth,
      yMin: extents.min,
      yMax: extents.max,
    })

    return {
      yMin: extents.min,
      yMax: extents.max,
      yTicks: tickPositions,
      pitchPaths: pitchGeometry.paths,
      pitchRects: pitchGeometry.rects,
      comfortablePaths: comfortableGeometry.paths,
      comfortableRects: comfortableGeometry.rects,
      hitTargets: points.map((point) => {
        const centerX = PADDING_X + clamp(point.x, 0, 1) * innerW
        const width = Math.max(POINT_HIT_SIZE, columnWidth)
        return {
          point,
          centerX,
          x: clamp(centerX - width / 2, 0, plotContentWidth - width),
          width,
        }
      }),
    }
  }, [plotContentWidth, points, totalDays])

  const xLabelSlots = useMemo(
    () =>
      plotContentWidth > 0
        ? buildXLabelSlots({ xLabels, plotContentWidth })
        : [],
    [plotContentWidth, xLabels],
  )

  useEffect(() => {
    if (plotContentWidth <= plotViewportWidth) {
      return
    }

    const frame = requestAnimationFrame(() => {
      scrollViewRef.current?.scrollTo({
        x: plotContentWidth - plotViewportWidth,
        animated: false,
      })
    })

    return () => {
      cancelAnimationFrame(frame)
    }
  }, [plotContentWidth, plotViewportWidth])

  const activeSelectedPoint =
    selectedPoint != null && points.includes(selectedPoint.point)
      ? selectedPoint
      : null
  const selectedHitTarget =
    activeSelectedPoint == null
      ? null
      : (hitTargets.find(
          (target) => target.point === activeSelectedPoint.point,
        ) ?? null)
  const overlayLeft =
    activeSelectedPoint == null
      ? 0
      : clampChartOverlayValue(
          activeSelectedPoint.anchorX - OVERLAY_WIDTH / 2,
          OVERLAY_MARGIN,
          Math.max(
            OVERLAY_MARGIN,
            windowWidth - OVERLAY_WIDTH - OVERLAY_MARGIN,
          ),
        )
  const overlayTop =
    activeSelectedPoint == null
      ? 0
      : clampChartOverlayValue(
          activeSelectedPoint.anchorY - OVERLAY_HEIGHT - 10,
          OVERLAY_MARGIN,
          Math.max(
            OVERLAY_MARGIN,
            windowHeight - OVERLAY_HEIGHT - OVERLAY_MARGIN,
          ),
        )

  const handleSelectPoint = (point: VoiceAssessmentRangePoint, x: number) => {
    setSelectedPoint({
      point,
      anchorX: x - scrollXRef.current,
      anchorY: CHART_HEIGHT / 2,
      viewportX: 0,
      viewportY: 0,
      scrollX: scrollXRef.current,
    })
    chartViewportRef.current?.measureInWindow((pageX, pageY) => {
      setSelectedPoint({
        point,
        anchorX: pageX + x - scrollXRef.current,
        anchorY: pageY + CHART_HEIGHT / 2,
        viewportX: pageX,
        viewportY: pageY,
        scrollX: scrollXRef.current,
      })
    })
  }

  return (
    <View className="w-full flex-row">
      <Modal
        transparent
        visible={activeSelectedPoint != null}
        animationType="fade"
      >
        <View className="flex-1">
          <Pressable
            className="absolute inset-0"
            testID="range-chart-overlay-dismiss"
            onPress={() => setSelectedPoint(null)}
          />
          {activeSelectedPoint == null ? null : (
            <>
              {hitTargets.map((target, index) => (
                <Pressable
                  key={`modal-hit-range-${String(index)}`}
                  testID={`range-chart-modal-point-${String(index)}`}
                  accessibilityRole="button"
                  onPress={() =>
                    handleSelectPoint(target.point, target.centerX)
                  }
                  style={{
                    position: 'absolute',
                    left:
                      activeSelectedPoint.viewportX +
                      target.x -
                      activeSelectedPoint.scrollX,
                    top: activeSelectedPoint.viewportY,
                    width: target.width,
                    height: CHART_HEIGHT,
                  }}
                />
              ))}
              <Pressable
                className="rounded-lg border border-border bg-background-secondary px-3 py-2 shadow-sm"
                testID="range-chart-overlay"
                onPress={(event) => event.stopPropagation()}
                style={{
                  position: 'absolute',
                  left: overlayLeft,
                  top: overlayTop,
                  width: OVERLAY_WIDTH,
                  minHeight: OVERLAY_HEIGHT,
                }}
              >
                <View className="gap-1.5">
                  <View className="flex-row items-baseline justify-between gap-3">
                    <Text
                      className="min-w-0 flex-1"
                      numberOfLines={1}
                      maxFontSizeMultiplier={1}
                      style={[label3, { color: colors.textBody }]}
                    >
                      {t('statistics:voice_assessment_comfortable_range_label')}
                    </Text>
                    <Text
                      numberOfLines={1}
                      maxFontSizeMultiplier={1}
                      style={[body3, { color: colors.textHeader }]}
                    >
                      {rangeLabel(activeSelectedPoint.point.comfortable)}
                    </Text>
                  </View>
                  <View className="flex-row items-baseline justify-between gap-3">
                    <Text
                      className="min-w-0 flex-1"
                      numberOfLines={1}
                      maxFontSizeMultiplier={1}
                      style={[label3, { color: colors.textBody }]}
                    >
                      {t('statistics:voice_assessment_pitch_range_label')}
                    </Text>
                    <Text
                      numberOfLines={1}
                      maxFontSizeMultiplier={1}
                      style={[body3, { color: colors.textHeader }]}
                    >
                      {rangeLabel(activeSelectedPoint.point.pitch)}
                    </Text>
                  </View>
                </View>
                <Text
                  className="mt-2"
                  numberOfLines={1}
                  maxFontSizeMultiplier={1}
                  style={[body4, { color: colors.textMuted }]}
                >
                  {formatVoiceAssessmentChartDate(
                    activeSelectedPoint.point.created,
                    locale,
                  )}
                </Text>
              </Pressable>
            </>
          )}
        </View>
      </Modal>
      <View>
        <View
          className="justify-between py-0.5"
          style={{ width: Y_AXIS_WIDTH, height: CHART_HEIGHT }}
        >
          {yTicks.map((tick, index) => (
            <Text
              key={`note-axis-${String(tick)}-${String(index)}`}
              className="text-right pr-1"
              style={[body4, { color: colors.textMuted }]}
              numberOfLines={1}
            >
              {noteLabelForPosition(tick)}
            </Text>
          ))}
        </View>
        <View style={{ width: Y_AXIS_WIDTH, height: X_AXIS_LABEL_HEIGHT }} />
      </View>
      <View
        ref={chartViewportRef}
        className="min-w-0 flex-1"
        testID="range-chart-viewport"
        onLayout={onChartLayout}
      >
        {plotViewportWidth > 0 && plotContentWidth > 0 ? (
          <ScrollView
            ref={scrollViewRef}
            horizontal
            showsHorizontalScrollIndicator={false}
            bounces={false}
            scrollEventThrottle={16}
            onScroll={(event) => {
              scrollXRef.current = event.nativeEvent.contentOffset.x
            }}
          >
            <View style={{ width: plotContentWidth }}>
              <View style={{ height: CHART_HEIGHT }}>
                <Svg width={plotContentWidth} height={CHART_HEIGHT}>
                  {yTicks.map((tick, index) => {
                    const y = yForPosition({
                      position: tick,
                      yMin,
                      yMax,
                    })
                    return (
                      <Line
                        key={`grid-${String(tick)}-${String(index)}`}
                        x1={0}
                        y1={y}
                        x2={plotContentWidth}
                        y2={y}
                        stroke={GRID_COLOR}
                        strokeWidth={1}
                        strokeDasharray="4 4"
                      />
                    )
                  })}
                  {pitchPaths.map((path, index) => (
                    <Path
                      key={`pitch-path-fill-${path.id}`}
                      testID={`range-chart-pitch-path-${String(index)}`}
                      d={path.fillD}
                      fill={RANGE_OUTER_FILL}
                    />
                  ))}
                  {pitchPaths.map((path) => (
                    <Path
                      key={`pitch-path-top-${path.id}`}
                      d={path.topD}
                      fill="none"
                      stroke={RANGE_OUTER_STROKE}
                      strokeWidth={OUTER_STROKE_WIDTH}
                    />
                  ))}
                  {pitchPaths.map((path) => (
                    <Path
                      key={`pitch-path-bottom-${path.id}`}
                      d={path.bottomD}
                      fill="none"
                      stroke={RANGE_OUTER_STROKE}
                      strokeWidth={OUTER_STROKE_WIDTH}
                    />
                  ))}
                  {pitchRects.map((rect, index) => (
                    <Rect
                      key={`pitch-rect-${rect.id}`}
                      testID={`range-chart-pitch-rect-${String(index)}`}
                      x={rect.x}
                      y={rect.y}
                      width={rect.width}
                      height={rect.height}
                      fill={RANGE_OUTER_FILL}
                      stroke={RANGE_OUTER_STROKE}
                      strokeWidth={OUTER_STROKE_WIDTH}
                    />
                  ))}
                  {comfortablePaths.map((path, index) => (
                    <Path
                      key={`comfortable-path-fill-${path.id}`}
                      testID={`range-chart-comfortable-path-${String(index)}`}
                      d={path.fillD}
                      fill={RANGE_INNER_FILL}
                    />
                  ))}
                  {comfortablePaths.map((path) => (
                    <Path
                      key={`comfortable-path-top-${path.id}`}
                      d={path.topD}
                      fill="none"
                      stroke={RANGE_INNER_STROKE}
                      strokeWidth={INNER_STROKE_WIDTH}
                    />
                  ))}
                  {comfortablePaths.map((path) => (
                    <Path
                      key={`comfortable-path-bottom-${path.id}`}
                      d={path.bottomD}
                      fill="none"
                      stroke={RANGE_INNER_STROKE}
                      strokeWidth={INNER_STROKE_WIDTH}
                    />
                  ))}
                  {comfortableRects.map((rect, index) => (
                    <Rect
                      key={`comfortable-rect-${rect.id}`}
                      testID={`range-chart-comfortable-rect-${String(index)}`}
                      x={rect.x}
                      y={rect.y}
                      width={rect.width}
                      height={rect.height}
                      fill={RANGE_INNER_FILL}
                      stroke={RANGE_INNER_STROKE}
                      strokeWidth={INNER_STROKE_WIDTH}
                    />
                  ))}
                  {selectedHitTarget == null ? null : (
                    <Line
                      testID="range-chart-selection-line"
                      x1={selectedHitTarget.centerX}
                      y1={0}
                      x2={selectedHitTarget.centerX}
                      y2={CHART_HEIGHT}
                      stroke={colors.textMuted}
                      strokeOpacity={0.72}
                      strokeWidth={1.5}
                      strokeDasharray="3 5"
                    />
                  )}
                </Svg>
                {hitTargets.map((target, index) => (
                  <Pressable
                    key={`hit-range-${String(index)}`}
                    testID={`range-chart-point-${String(index)}`}
                    accessibilityRole="button"
                    onPress={() =>
                      handleSelectPoint(target.point, target.centerX)
                    }
                    style={{
                      position: 'absolute',
                      left: target.x,
                      top: 0,
                      width: target.width,
                      height: CHART_HEIGHT,
                    }}
                  />
                ))}
              </View>
              <View style={{ height: X_AXIS_LABEL_HEIGHT }}>
                {xLabelSlots.map((label) => {
                  return (
                    <View
                      key={`${label.line1}-${String(label.index)}`}
                      className="items-center px-0.5 pt-1.5"
                      testID={`range-chart-x-label-${String(label.index)}`}
                      style={{
                        position: 'absolute',
                        left: label.left,
                        width: label.width,
                      }}
                    >
                      <Text
                        className="text-center"
                        numberOfLines={1}
                        ellipsizeMode="clip"
                        maxFontSizeMultiplier={1}
                        style={[body4, { color: colors.textMuted }]}
                      >
                        {label.line1.length > 3
                          ? label.line1.slice(0, 3)
                          : label.line1}
                      </Text>
                      {label.line2 != null && label.line2.length > 0 ? (
                        <Text
                          className="mt-0.5 text-center"
                          numberOfLines={1}
                          ellipsizeMode="clip"
                          maxFontSizeMultiplier={1}
                          style={[body4, { color: colors.textMuted }]}
                        >
                          {label.line2}
                        </Text>
                      ) : null}
                    </View>
                  )
                })}
              </View>
            </View>
          </ScrollView>
        ) : null}
      </View>
    </View>
  )
}
